import { Alert, ArrowLeftIcon, Button, useToast } from '@razorpay/blade/components';
import api from 'api';
import { getErrorsAsString } from 'components/Kyc/utils';
import { routePaths } from 'components/Routes/data';
import { perquisitesValidationSchema } from 'components/WizardViews/SalaryComponents/constants';
import { PerquisitesFormData } from 'components/WizardViews/SalaryComponents/types';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
} from 'components/WizardViews/components';
import {
  ReviewSection,
  ReviewSectionContainer,
  ReviewSectionHeader,
  ReviewSectionItem,
  ReviewSectionItemContainer,
} from 'components/WizardViews/components/review';
import { useFormWithProvider } from 'forms';
import { FormEvent, useMemo } from 'react';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { AppError } from 'utils/AppError';

const ReviewStep = () => {
  const { formData, setIsSubmitting, validate } = useFormWithProvider<PerquisitesFormData>();
  const history = useHistory();
  const toast = useToast();

  const createPerquisitesComponent = useMutation({
    mutationFn: api.salaryComponents.mutations.createPerquisitesComponent,
    onError: (e: AppError) => {
      toast.show({
        content: 'There was an error in creating salary component.',
        color: 'negative',
        duration: 3000,
      });
    },
    onMutate() {
      setIsSubmitting(true);
    },
    onSettled() {
      setIsSubmitting(false);
    },
    onSuccess(data) {
      toast.show({
        content: 'Salary component created successfully!',
        color: 'positive',
      });
      history.push(routePaths.salaryComponents.perquisitesListView);
    },
  });

  const editPerquisitesComponentMutation = useMutation({
    mutationFn: api.salaryComponents.mutations.editPerquisitesComponent,
    onError: (e: AppError) => {
      toast.show({
        content: 'There was an error in editing salary component.',
        color: 'negative',
      });
    },
    onSuccess(data) {
      toast.show({
        content: 'Salary component updated successfully!',
        color: 'positive',
      });
      history.push(routePaths.salaryComponents.perquisitesListView);
    },
  });

  const isFormValid = useMemo(
    () => validate(perquisitesValidationSchema, { sideEffects: false }),
    [validate],
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const payload = {
      name: formData.name || '',
      displayName: formData.displayName || '',
      description: formData.description || '',
      isSystemDefault: formData.isSystemDefault || false,
      status: formData.status || 'active',
      showZero: formData.showZero || false,
      visibleToEmployee: formData.visibleToEmployee || true,
      ...(formData.id && { id: formData.id }),
    };

    createPerquisitesComponent.mutate({
      components: [payload],
    });
  };

  const editGeneralDetailsClick = () =>
    history.push({
      pathname: routePaths.salaryComponents.create.perquisites.generalDetails,
    });

  const isEditMode = Boolean(formData.id);

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper width="594px">
        <StepHeader title="Review" subtitle="Component" marginBottom={'spacing.9'} />
        {(createPerquisitesComponent.isError || editPerquisitesComponentMutation.isError) && (
          <Alert
            marginBottom="spacing.7"
            title="The component cannot be created because of the below errors:"
            description={getErrorsAsString(
              createPerquisitesComponent.error ||
                editPerquisitesComponentMutation.error ||
                new AppError({ messageForUser: 'Unknown error' }),
            )}
            color="negative"
            isFullWidth
            isDismissible={false}
          />
        )}
        <ReviewSectionContainer>
          <ReviewSection>
            <ReviewSectionHeader
              title="General"
              subtitle="details"
              onEditClick={editGeneralDetailsClick}
            />
            <ReviewSectionItemContainer>
              <ReviewSectionItem
                label="Component Name"
                value={formData.name}
                status={formData.name ? 'success' : 'error'}
              />
              <ReviewSectionItem
                label="Payslip & Salary Register name"
                value={formData.displayName}
                status={formData.displayName ? 'success' : 'error'}
              />
              {formData.description ? (
                <ReviewSectionItem label="Component Description" value={formData.description} />
              ) : null}
            </ReviewSectionItemContainer>
          </ReviewSection>
        </ReviewSectionContainer>
      </StepContentWrapper>
      <StepActionsWrapper width="594px">
        <Button
          variant="secondary"
          icon={ArrowLeftIcon}
          onClick={() => history.goBack()}
          isDisabled={
            createPerquisitesComponent.isLoading || editPerquisitesComponentMutation.isLoading
          }>
          General
        </Button>
        <Button
          type="submit"
          isLoading={
            createPerquisitesComponent.isLoading || editPerquisitesComponentMutation.isLoading
          }
          isDisabled={!isFormValid}>
          {isEditMode ? 'Update Component' : 'Create Component'}
        </Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default ReviewStep;
